import { useEffect, useId, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { pathFor, type RouteId } from "../../../app/router/routes";
import type { MarketingCopy, SectionKey } from "../copy";
import { GAME_PAGE_IDS, marketingGame, type MarketingGame } from "../games";
import { requestSection, type SectionSpy } from "../useSectionSpy";
import { ArrowRightIcon, ChevronRightIcon, CloseIcon, LockIcon, MenuIcon } from "../icons";

/**
 * The reading-progress rule is the logo's own colour band, run the width of the
 * screen — so it reads as the brand rather than as a second pink call to action.
 */
const LOGO_BAND =
  "linear-gradient(to right," +
  " var(--color-teal)," +
  " var(--color-sunshine) 26%," +
  " var(--color-leaf) 50%," +
  " var(--color-primary-bright) 80%," +
  " var(--color-sunshine))";

/** The four games with a page, in the documented order — the dropdown's rows. */
const MENU_GAMES: readonly MarketingGame[] = GAME_PAGE_IDS.map(marketingGame);

export interface SiteHeaderProps {
  t: MarketingCopy;
  spy: SectionSpy;
  /** "Log in" — opens the door picker (`#/start`). */
  onSignIn?: () => void;
  /**
   * The page this header sits on is not the home page (a game page, For schools,
   * Connect), so a section link opens the home page and scrolls there instead —
   * through `requestSection`, which the home page's spy picks up.
   */
  standalone?: boolean;
  /**
   * The header floats over a full-bleed hero: a frosted pill at the top of the
   * page, the solid bar once the reader scrolls. Without it (Connect) the header
   * is the solid bar throughout, sticky, taking its own space in the flow.
   */
  overlay?: boolean;
  /** The route this page is — lights up "For schools", or "Games" on a game page. */
  current?: RouteId;
}

/**
 * The website's header — the home page, the game pages, For schools, Connect.
 *
 * 🔴 THREE DESTINATIONS, AND THEY ARE THE THREE A BUYER ASKS FOR FIRST: the
 * games (a dropdown of the four game pages), how it works, and the page for
 * schools. Privacy and the FAQ live lower down — privacy has its own panel on
 * the home page and a link in the footer, and the FAQ ends the home page, where
 * a reader with questions left over already is.
 *
 * 🔴 ONE CONVERSION, AND IT IS THE PINK ONE. "Book a demo" is the only filled
 * button; "Log in" is a quiet text button beside it.
 *
 * ⚠️ SECTIONS ARE `<button>`s, ROUTES ARE `<a>`s, AND THE SPLIT IS NOT
 * COSMETIC. `#games` written to `location.hash` fires `hashchange`, fails
 * `matchRoute` and pushes a junk entry into the back stack — `useSectionSpy`
 * records that failure in full. On a standalone page a section link IS an
 * `<a href="#/">`, because there it genuinely navigates — to the home page.
 *
 * ⚠️ THE GAMES MENU IS A DISCLOSURE, NOT AN ARIA `menu`. A `menu` promises
 * arrow-key navigation and application semantics; this is a button that shows a
 * list of links, which every screen reader and keyboard already understands.
 * Escape and a click outside both close it.
 */
export function SiteHeader({
  t,
  spy,
  onSignIn,
  standalone = false,
  overlay = false,
  current,
}: SiteHeaderProps) {
  const [open, setOpen] = useState(false);
  const [gamesOpen, setGamesOpen] = useState(false);
  const [sheetGamesOpen, setSheetGamesOpen] = useState(false);
  const menuId = useId();
  const gamesId = useId();
  const gamesRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<number | null>(null);
  /*
   * ⚠️ A MOUSE HOVERS BEFORE IT CLICKS. Hover opens the menu, so the click that
   * follows must not toggle it shut again — that read as a menu that flashed
   * open and closed under the pointer. A click after a hover-open is absorbed;
   * keyboard and touch never hover, so their click still toggles.
   */
  const openedByHover = useRef(false);

  // Escape closes whichever panel is open.
  useEffect(() => {
    if (!open && !gamesOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        setGamesOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, gamesOpen]);

  // A click anywhere outside the games panel closes it.
  useEffect(() => {
    if (!gamesOpen) return;
    const onDown = (e: PointerEvent): void => {
      if (gamesRef.current && !gamesRef.current.contains(e.target as Node)) setGamesOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("pointerdown", onDown);
    };
  }, [gamesOpen]);

  const go = (key: SectionKey): void => {
    setOpen(false);
    setGamesOpen(false);
    if (standalone) {
      requestSection(key);
      return;
    }
    spy.goTo(key);
  };

  /*
   * Hover opens the menu on a mouse, with a short grace period on the way out so
   * a pointer crossing the gap between button and panel does not close it.
   * Touch and keyboard use the button itself.
   */
  const hoverOpen = (e: ReactPointerEvent): void => {
    if (e.pointerType !== "mouse") return;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    if (!gamesOpen) openedByHover.current = true;
    setGamesOpen(true);
  };
  const hoverClose = (e: ReactPointerEvent): void => {
    if (e.pointerType !== "mouse") return;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => {
      openedByHover.current = false;
      setGamesOpen(false);
    }, 160);
  };
  const toggleGames = (): void => {
    if (openedByHover.current) {
      openedByHover.current = false;
      return;
    }
    setGamesOpen((v) => !v);
  };

  const solid = !overlay || spy.scrolled || open;
  const unread = ((1 - spy.progress) * 100).toFixed(1);
  const onGamePage = current !== undefined && GAME_PAGE_IDS.includes(current as MarketingGame["id"]);
  const gamesActive = onGamePage || (!standalone && spy.active === "games");

  return (
    <header
      className={`kq-header-in z-50 ${overlay ? "fixed inset-x-0 top-0" : "sticky top-0"} transition-[padding] duration-300 ease-dash ${
        solid ? "pt-0" : "px-3 pt-3 sm:px-6 sm:pt-5 lg:px-10"
      }`}
    >
      <div
        className={`mx-auto flex w-full items-center justify-between gap-4 transition-all duration-300 ease-dash ${
          solid
            ? "h-16 max-w-none border-b border-line bg-surface/95 px-5 backdrop-blur-xl sm:px-6 lg:h-18 lg:px-[max(2.5rem,calc((100vw_-_1240px)/2_+_2.5rem))]"
            : "h-16 max-w-[1240px] rounded-pill border border-surface/90 bg-surface/85 pr-2 pl-5 shadow-raised backdrop-blur-xl sm:pl-7 lg:h-19"
        }`}
      >
        <a
          href="#/"
          className="shrink-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-surface"
          aria-label={t.homeLabel}
        >
          <img
            src="/images/kalqy-brand-logo.png"
            alt="Kalqy"
            width={896}
            height={264}
            className="h-8 w-auto object-contain lg:h-9"
          />
        </a>

        <nav aria-label={t.navLabel} className="hidden items-center gap-1 lg:flex">
          {/* ── Games: a dropdown of the four game pages ──────────────── */}
          <div ref={gamesRef} className="relative" onPointerEnter={hoverOpen} onPointerLeave={hoverClose}>
            <button
              type="button"
              aria-expanded={gamesOpen}
              aria-controls={gamesId}
              onClick={toggleGames}
              className={`${NAV_ITEM} inline-flex items-center gap-1.5 ${gamesActive ? NAV_ACTIVE : NAV_IDLE}`}
            >
              {t.nav.games}
              <ChevronRightIcon
                className={`h-4 w-4 transition-transform duration-200 ease-dash ${gamesOpen ? "-rotate-90" : "rotate-90"}`}
              />
            </button>
            <div
              id={gamesId}
              hidden={!gamesOpen}
              className="absolute top-full left-1/2 w-[34rem] -translate-x-1/2 pt-3"
            >
              <div className="rounded-xl border border-line bg-surface p-3 shadow-[0_24px_60px_-20px_color-mix(in_srgb,var(--color-ink)_35%,transparent)]">
                <p className="px-2 pt-1 pb-2 font-dash text-dash-xs font-bold text-muted">{t.gamesMenu.label}</p>
                <ul className="grid grid-cols-2 gap-1.5">
                  {MENU_GAMES.map((game) => (
                    <li key={game.id}>
                      <GameMenuLink
                        t={t}
                        game={game}
                        active={current === game.id}
                        onNavigate={() => {
                          setGamesOpen(false);
                        }}
                      />
                    </li>
                  ))}
                </ul>
                <a
                  href="#/"
                  onClick={(e) => {
                    if (!standalone) e.preventDefault();
                    go("games");
                  }}
                  className="mt-2 flex items-center justify-between rounded-lg bg-canvas px-4 py-3 font-dash text-dash-sm font-extrabold text-ink outline-none transition-colors duration-150 ease-dash hover:bg-primary-tint hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {t.gamesMenu.seeAll}
                  <ArrowRightIcon className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>

          <SectionLink
            label={t.nav.how}
            active={!standalone && spy.active === "how"}
            standalone={standalone}
            onClick={() => {
              go("how");
            }}
          />
          <a
            href={pathFor("for-schools")}
            aria-current={current === "for-schools" ? "page" : undefined}
            className={`${NAV_ITEM} ${current === "for-schools" ? NAV_ACTIVE : NAV_IDLE}`}
          >
            {t.nav.schools}
          </a>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            data-testid="sign-in"
            onClick={onSignIn}
            className="hidden rounded-pill px-4 py-2.5 font-dash text-dash-sm font-extrabold text-ink outline-none transition-colors duration-150 ease-dash hover:bg-primary-tint hover:text-primary focus-visible:ring-2 focus-visible:ring-primary sm:inline-flex"
          >
            {t.logIn}
          </button>
          <a
            href={pathFor("contact-sales")}
            className="inline-flex h-11 items-center gap-2 rounded-pill bg-primary px-5 font-dash text-dash-sm font-extrabold text-primary-ink shadow-[0_4px_0_var(--color-primary-deep)] outline-none transition duration-150 ease-dash hover:-translate-y-px hover:bg-primary-bright focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 active:translate-y-0.5 active:shadow-[0_1px_0_var(--color-primary-deep)]"
          >
            {t.cta.bookDemo}
            <ArrowRightIcon className="hidden h-4 w-4 sm:block" />
          </a>
          <button
            type="button"
            aria-label={open ? t.closeMenu : t.openMenu}
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => {
              setOpen((v) => !v);
            }}
            className={`flex h-11 w-11 items-center justify-center rounded-pill border transition-colors duration-150 ease-dash lg:hidden ${
              open ? "border-primary bg-primary text-primary-ink" : "border-line bg-canvas text-ink hover:bg-line/50"
            }`}
          >
            {open ? <CloseIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* The reading-progress rule — revealed by clip-path, so it never re-lays-out the header. */}
      {solid ? (
        <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden" aria-hidden="true">
          <div className="h-full w-full" style={{ backgroundImage: LOGO_BAND, clipPath: `inset(0 ${unread}% 0 0)` }} />
        </div>
      ) : null}

      {/*
        The phone sheet. `hidden` rather than unmounted, so `aria-controls` always
        names an element that exists. It scrolls on its own if a short phone
        cannot fit it.
      */}
      <div
        id={menuId}
        hidden={!open}
        className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-b border-line bg-surface px-5 pt-2 pb-6 shadow-raised lg:hidden"
      >
        <nav aria-label={t.navLabel} className="flex flex-col">
          <button
            type="button"
            aria-expanded={sheetGamesOpen}
            onClick={() => {
              setSheetGamesOpen((v) => !v);
            }}
            className={`${SHEET_ITEM} ${onGamePage ? "bg-primary-tint text-primary" : "text-ink hover:bg-canvas"}`}
          >
            {t.nav.games}
            <ChevronRightIcon
              className={`h-5 w-5 text-muted transition-transform duration-200 ease-dash ${sheetGamesOpen ? "-rotate-90" : "rotate-90"}`}
            />
          </button>
          {sheetGamesOpen ? (
            <ul className="mb-2 grid gap-1 pl-2">
              {MENU_GAMES.map((game) => (
                <li key={game.id}>
                  <GameMenuLink
                    t={t}
                    game={game}
                    active={current === game.id}
                    onNavigate={() => {
                      setOpen(false);
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : null}
          <SheetSection
            label={t.nav.how}
            standalone={standalone}
            onClick={() => {
              go("how");
            }}
          />
          <a
            href={pathFor("for-schools")}
            onClick={() => {
              setOpen(false);
            }}
            className={`${SHEET_ITEM} ${current === "for-schools" ? "bg-primary-tint text-primary" : "text-ink hover:bg-canvas"}`}
          >
            {t.nav.schools}
            <ChevronRightIcon className="h-5 w-5 text-muted" />
          </a>
        </nav>
        <div className="mt-3 flex flex-col gap-3 border-t border-line pt-5">
          <a
            href={pathFor("contact-sales")}
            onClick={() => {
              setOpen(false);
            }}
            className="flex h-13 items-center justify-center gap-2 rounded-pill bg-primary font-dash text-dash-md font-extrabold text-primary-ink shadow-[0_5px_0_var(--color-primary-deep)]"
          >
            {t.cta.bookSchoolDemo}
          </a>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onSignIn?.();
            }}
            className="flex h-13 items-center justify-center rounded-pill border-2 border-line bg-surface font-dash text-dash-md font-extrabold text-ink"
          >
            {t.logIn}
          </button>
        </div>
      </div>
    </header>
  );
}

const NAV_ITEM =
  "rounded-pill px-4 py-2 font-dash text-dash-sm font-extrabold outline-none transition-colors duration-150 ease-dash focus-visible:ring-2 focus-visible:ring-primary";
const NAV_ACTIVE = "bg-primary text-primary-ink shadow-[0_3px_0_var(--color-primary-deep)]";
const NAV_IDLE = "text-ink-soft hover:bg-primary-tint hover:text-primary";
const SHEET_ITEM =
  "flex w-full items-center justify-between rounded-lg px-3 py-3.5 text-left font-dash text-dash-md font-extrabold transition-colors duration-150 ease-dash";

/** A section of the home page — a scroll there, or (on another page) a link home that scrolls on arrival. */
function SectionLink({
  label,
  active,
  standalone,
  onClick,
}: {
  label: string;
  active: boolean;
  standalone: boolean;
  onClick: () => void;
}) {
  const className = `${NAV_ITEM} ${active ? NAV_ACTIVE : NAV_IDLE}`;
  if (standalone) {
    return (
      <a href="#/" onClick={onClick} className={className}>
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-current={active ? "true" : undefined} className={className}>
      {label}
    </button>
  );
}

function SheetSection({ label, standalone, onClick }: { label: string; standalone: boolean; onClick: () => void }) {
  const className = `${SHEET_ITEM} text-ink hover:bg-canvas`;
  const chevron = <ChevronRightIcon className="h-5 w-5 text-muted" />;
  if (standalone) {
    return (
      <a href="#/" onClick={onClick} className={className}>
        {label}
        {chevron}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {label}
      {chevron}
    </button>
  );
}

/** One game in the menu: its art, its name, its skill, and whether a stranger can play it. */
function GameMenuLink({
  t,
  game,
  active,
  onNavigate,
}: {
  t: MarketingCopy;
  game: MarketingGame;
  active: boolean;
  onNavigate: () => void;
}) {
  return (
    <a
      href={pathFor(game.id as RouteId)}
      aria-current={active ? "page" : undefined}
      onClick={onNavigate}
      className={`group flex items-center gap-3 rounded-lg p-2 outline-none transition-colors duration-150 ease-dash focus-visible:ring-2 focus-visible:ring-primary ${
        active ? "bg-primary-tint" : "hover:bg-canvas"
      }`}
    >
      <img
        src={game.image}
        alt=""
        width={96}
        height={64}
        loading="lazy"
        decoding="async"
        className="h-14 w-20 shrink-0 rounded-md object-cover"
      />
      <span className="min-w-0">
        <span className="block truncate font-kid text-dash-md leading-tight font-extrabold text-ink group-hover:text-primary">
          {game.title}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5 font-dash text-dash-xs font-bold text-muted">
          <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-pill ${game.dotClass}`} />
          {t.skills[game.skill]}
        </span>
        <span className="mt-0.5 flex items-center gap-1 font-dash text-dash-xs font-extrabold">
          {game.free ? (
            <span className="text-success">{t.games.freeBadge}</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-muted">
              <LockIcon className="h-3 w-3" />
              {t.games.lockedBadge}
            </span>
          )}
        </span>
      </span>
    </a>
  );
}
